import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveDynamicComponent as _resolveDynamicComponent, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "sticky top-0 z-40 flex h-20 flex-shrink-0 items-center bg-white dark:bg-gray-800 border-b border-gray-200/50 dark:border-gray-700/50 shadow-soft"
}
const _hoisted_2 = { class: "flex items-center gap-2 px-2" }
const _hoisted_3 = ["title"]
const _hoisted_4 = { class: "flex flex-1 justify-between px-4" }
const _hoisted_5 = { class: "ml-auto flex items-center md:ml-6" }
const _hoisted_6 = { class: "relative ml-3" }
const _hoisted_7 = ["src"]
const _hoisted_8 = { key: 1 }
const _hoisted_9 = {
  key: 0,
  class: "absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl bg-white dark:bg-gray-800 py-2 shadow-elegant-lg ring-1 ring-gray-200/50 dark:ring-gray-700/50 focus:outline-none",
  role: "menu"
}
const _hoisted_10 = { class: "px-4 py-3 text-sm" }
const _hoisted_11 = ["title"]
const _hoisted_12 = {
  key: 0,
  class: "text-xs text-gray-500 dark:text-gray-400 font-normal"
}
const _hoisted_13 = ["title"]
const _hoisted_14 = { class: "flex items-center" }
const _hoisted_15 = { class: "flex items-center" }
const _hoisted_16 = { class: "mr-3 text-lg flex-shrink-0 text-gray-400" }
const _hoisted_17 = { class: "truncate font-medium" }
const _hoisted_18 = ["disabled"]
const _hoisted_19 = { class: "flex items-center" }
const _hoisted_20 = { class: "truncate" }
const _hoisted_21 = { class: "mx-auto max-w-8xl px-4 sm:px-6 lg:px-8" }

import { ref, computed, watch, onMounted, onUnmounted, provide, } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useClickOutside } from '@/composables/ui/useClickOutside'
import { usePWAInstall } from '@/composables/ui/usePWAInstall'
import { useLogin } from '@/composables/auth/useLogin'
import { positionService } from '@/services/positionService'
import Sidebar from './Sidebar.vue'
import { Maximize2, Menu, LogOut, User } from 'lucide-vue-next'
import SunIcon from '@/components/icons/SunIcon.vue'
import MoonIcon from '@/components/icons/MoonIcon.vue'
import { useRoute } from 'vue-router'


export default /*@__PURE__*/_defineComponent({
  __name: 'AppLayout',
  setup(__props) {

// Imports
const route = useRoute()

const hideLayout = computed(
  () => route.name === 'view-online-form' || route.name === 'profile' || route.name === 'employee-view-online-form'
)

// Store Instances
const authStore = useAuthStore()
const themeStore = useThemeStore()
const router = useRouter()
// const { handleLogout } = useLogin()

// const role = ref('')
const email = ref('')
const password = ref('')
const rememberMe = ref(false)

const { handleLogout } = useLogin({ email, password, rememberMe })
const { isInstallable, promptInstall } = usePWAInstall()

// State
const isProfileOpen = ref(false)
const isSidebarOpen = ref(false)
const isSidebarCollapsed = ref(false)
// const dismissedInstall = ref(false)

// const dismissInstall = () => {
//   	dismissedInstall.value = true
// }

// Computed Properties
const currentUser = computed(() => authStore.currentUser)

const displayName = computed(() => {
  const u = currentUser.value
  if (!u) return ''
  return u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email?.split('@')[0] || ''
})

const isLoading = computed(() => authStore.isLoading)
const isDarkMode = computed(() => themeStore.isDarkMode)
const userInitials = computed(() => {
  const u = currentUser.value
  if (!u) return '?'

  const first = u.first_name?.trim() || ''
  const last = u.last_name?.trim() || ''
  if (first && last) return (first.charAt(0) + last.charAt(0)).toUpperCase()
  if (first) return first.charAt(0).toUpperCase()

  const name = u.name?.trim()
  if (name) {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    return parts[0].charAt(0).toUpperCase()
  }

  const email = u.email?.trim()
  if (email) return email.charAt(0).toUpperCase()

  return '?'
})

const isEmployee = computed(() => {
  	return currentUser.value?.role?.toLowerCase() === 'employee'
})


// Methods
const toggleProfile = () => {
  	isProfileOpen.value = !isProfileOpen.value
}

const toggleSidebar = () => {
  	isSidebarOpen.value = !isSidebarOpen.value
}

const toggleTheme = () => {
  	themeStore.toggleTheme()
}

const toggleSidebarCollapse = () => {
  	isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const goToProfile = () => {
	if (router.hasRoute('profile')) {
		router.push({ name: 'profile' })
	}
	isProfileOpen.value = false
}

// Lifecycle Hooks
const { handleClickOutside } = useClickOutside(isProfileOpen)

const resolvePosition = async () => {
	const user = authStore.currentUser
	if (!user || !user.position_id || user.position) return
	try {
		const positions = await positionService.getPositions()
		const matched = positions.find(p => p.id === user.position_id)
		
		if (matched) {
			user.position = matched.name
			const storage = sessionStorage.getItem('token') ? sessionStorage : localStorage
			if (storage.getItem('token')) {
				storage.setItem('user', JSON.stringify({ ...user }))
			}

			return user.position
		}
	} catch (err) {
		console.warn('Failed to resolve position:', err)
    	return user?.position
	}
}

watch(
	() => authStore.currentUser,
	async (user) => {
		if (!user) return
		await resolvePosition()
	},
	{ immediate: true }
)

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  	document.removeEventListener('click', handleClickOutside)
})

provide('isSidebarCollapsed', isSidebarCollapsed)
provide('toggleSidebarCollapse', toggleSidebarCollapse)

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950', { '!bg-white dark:!bg-gray-900': hideLayout.value }])
  }, [
    (!hideLayout.value)
      ? (_openBlock(), _createBlock(Sidebar, {
          key: 0,
          isOpen: isSidebarOpen.value,
          "onUpdate:isOpen": _cache[0] || (_cache[0] = ($event: any) => ((isSidebarOpen).value = $event)),
          isCollapsed: isSidebarCollapsed.value
        }, null, 8, ["isOpen", "isCollapsed"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      class: _normalizeClass(['transition-all duration-300', hideLayout.value ? '' : (isSidebarCollapsed.value ? 'lg:pl-20' : 'lg:pl-64')])
    }, [
      (!hideLayout.value)
        ? (_openBlock(), _createElementBlock("header", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("button", {
                type: "button",
                class: "px-4 text-gray-500 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden",
                onClick: toggleSidebar
              }, [
                _createVNode(_unref(Menu), { class: "h-6 w-6" })
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "hidden h-10 w-10 items-center justify-center rounded-full border border-gray-200/70 bg-white/80 text-gray-600 shadow-sm transition hover:text-primary hover:shadow-md dark:border-gray-700/60 dark:bg-gray-900/60 dark:text-gray-300 dark:hover:text-primary-dark lg:inline-flex",
                title: isSidebarCollapsed.value ? 'Expand sidebar' : 'Collapse sidebar',
                onClick: toggleSidebarCollapse
              }, [
                (_openBlock(), _createBlock(_resolveDynamicComponent(isSidebarCollapsed.value ? _unref(Maximize2) : _unref(Menu)), { class: "h-4 w-4" }))
              ], 8, _hoisted_3)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _cache[7] || (_cache[7] = _createElementVNode("div", { class: "flex flex-1" }, null, -1)),
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("div", null, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "flex max-w-xs items-center rounded-full bg-gradient-to-br from-primary to-primary-hover dark:from-primary-dark dark:to-primary-dark-hover text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 dark:focus:ring-primary-dark/50 focus:ring-offset-2 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105",
                      onClick: toggleProfile
                    }, [
                      _cache[3] || (_cache[3] = _createElementVNode("span", { class: "sr-only" }, "Open user menu", -1)),
                      _createElementVNode("div", {
                        class: _normalizeClass(["h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold ring-2 ring-white/30 dark:ring-white/20 overflow-hidden", currentUser.value?.profile_picture ? '' : 'bg-white/20 dark:bg-white/10 backdrop-blur-sm'])
                      }, [
                        (currentUser.value?.profile_picture)
                          ? (_openBlock(), _createElementBlock("img", {
                              key: 0,
                              src: currentUser.value.profile_picture,
                              alt: "",
                              class: "h-full w-full object-cover"
                            }, null, 8, _hoisted_7))
                          : (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(userInitials.value), 1))
                      ], 2)
                    ])
                  ]),
                  (isProfileOpen.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createElementVNode("div", _hoisted_10, [
                          _createElementVNode("p", {
                            class: "font-semibold text-gray-900 dark:text-gray-100 truncate",
                            title: displayName.value
                          }, [
                            _createTextVNode(_toDisplayString(displayName.value) + " ", 1),
                            (currentUser.value?.position)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_12, "(" + _toDisplayString(currentUser.value.position) + ")", 1))
                              : _createCommentVNode("", true)
                          ], 8, _hoisted_11),
                          _createElementVNode("p", {
                            class: "text-gray-500 dark:text-gray-400 truncate mt-0.5",
                            title: currentUser.value?.email
                          }, _toDisplayString(currentUser.value?.email), 9, _hoisted_13)
                        ]),
                        _cache[6] || (_cache[6] = _createElementVNode("div", { class: "border-t border-gray-200/50 dark:border-gray-700/50 my-1" }, null, -1)),
                        (isEmployee.value)
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 0,
                              type: "button",
                              class: "block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-100/80 dark:focus:bg-gray-700/50 transition-colors duration-200 rounded-lg mx-1",
                              onClick: goToProfile
                            }, [
                              _createElementVNode("div", _hoisted_14, [
                                _createVNode(_unref(User), { class: "mr-3 h-5 w-5 text-gray-400 flex-shrink-0" }),
                                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "truncate font-medium" }, "Profile", -1))
                              ])
                            ]))
                          : _createCommentVNode("", true),
                        (_unref(isInstallable))
                          ? (_openBlock(), _createElementBlock("button", {
                              key: 1,
                              type: "button",
                              class: "block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-100/80 dark:focus:bg-gray-700/50 transition-colors duration-200 rounded-lg mx-1",
                              onClick: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(promptInstall) && _unref(promptInstall)(...args)))
                            }, [...(_cache[5] || (_cache[5] = [
                              _createElementVNode("div", { class: "flex items-center" }, [
                                _createElementVNode("svg", {
                                  class: "mr-3 h-5 w-5 text-gray-400 flex-shrink-0",
                                  xmlns: "http://www.w3.org/2000/svg",
                                  fill: "none",
                                  viewBox: "0 0 24 24",
                                  stroke: "currentColor",
                                  "stroke-width": "2"
                                }, [
                                  _createElementVNode("path", {
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    d: "M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                  })
                                ]),
                                _createElementVNode("span", { class: "truncate font-medium text-primary dark:text-primary-dark" }, "Install App")
                              ], -1)
                            ]))]))
                          : _createCommentVNode("", true),
                        _createElementVNode("button", {
                          type: "button",
                          class: "block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100/80 dark:hover:bg-gray-700/50 focus:outline-none focus:bg-gray-100/80 dark:focus:bg-gray-700/50 transition-colors duration-200 rounded-lg mx-1",
                          onClick: toggleTheme
                        }, [
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("span", _hoisted_16, [
                              (isDarkMode.value)
                                ? (_openBlock(), _createBlock(SunIcon, { key: 0 }))
                                : (_openBlock(), _createBlock(MoonIcon, {
                                    key: 1,
                                    class: "rotate-180"
                                  }))
                            ]),
                            _createElementVNode("span", _hoisted_17, _toDisplayString(isDarkMode.value ? 'Light Mode' : 'Dark Mode'), 1)
                          ])
                        ]),
                        _createElementVNode("button", {
                          type: "button",
                          class: "block w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:bg-red-50 dark:focus:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 rounded-lg mx-1",
                          onClick: _cache[2] || (_cache[2] = 
//@ts-ignore
(...args) => (_unref(handleLogout) && _unref(handleLogout)(...args))),
                          disabled: isLoading.value
                        }, [
                          _createElementVNode("div", _hoisted_19, [
                            _createVNode(_unref(LogOut), { class: "mr-3 h-5 w-5 text-gray-400 flex-shrink-0" }),
                            _createElementVNode("span", _hoisted_20, _toDisplayString(isLoading.value ? 'Logging out...' : 'Sign out'), 1)
                          ])
                        ], 8, _hoisted_18)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ])
            ])
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("main", {
        class: _normalizeClass(!hideLayout.value ? 'pt-8 sm:pt-10' : '')
      }, [
        _createVNode(_Transition, { name: "fade" }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_21, [
              _createVNode(_component_router_view)
            ])
          ]),
          _: 1
        })
      ], 2)
    ], 2)
  ], 2))
}
}

})